import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Chip, Typography, useTheme, useMediaQuery, ListItemButton, Box, styled, Button } from '@mui/material'
import { Flight, Group, Star, Euro, Dashboard, Settings, ChevronLeft, ChevronRight, Add, FlightTakeoff, AssignmentTurnedIn, PlayArrow } from '@mui/icons-material'
import Logo from '../../images/logo_nobg.png'
import * as ROUTES from '../../_constants/routes'
import { useSelector } from 'react-redux'
import NewFlightDialog from '../flights/NewFlightDialog'
import { getQualificationStatus } from '../../_helpers/qualificationHelper'
import { chain } from 'lodash'
import { useLocation, useNavigate } from 'react-router-dom'
import { MENU_STATUS_BIG, MENU_STATUS_HIDDEN, MENU_STATUS_SMALL, QUALIFICATION_COLLECTION } from '../../_constants/globals'
import { useFirestore } from '../../hooks/useFirestore'
import useAuth from '../../hooks/useAuth'


const listItemStyles = {
  py: 2,
  borderBottom: 'solid 1px',
  borderColor: 'grey.800',
}

const StyledListItemText = styled(ListItemText)(() => ({
  fontSize: 'inherit',
  marginTop: 0,
  marginBottom: 0,
  display: 'inline-block',
  verticalAlign: 'top',
}))

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  color: 'inherit',
  marginRight: theme.spacing(2),
  minWidth: 'auto',
}))

const chevronStyle = {
  transition: 'transform .3s cubic-bezier(0.4, 0, 0.2, 1),color .1s cubic-bezier(0.4, 0, 0.2, 1)',
}

const fixedDecimals = num => Math.round(num * 100) / 100

const NavigationDrawer = ({ drawerWidth, menuStatus, setMenuStatus }) => {
  
  const theme = useTheme()
  const isUpMd = useMediaQuery(theme.breakpoints.up('md'))
  const profile = useAuth().getProfile()
  const qualificationHooks = useFirestore(QUALIFICATION_COLLECTION)
  const dbQualifications = qualificationHooks.getDocs()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const adminMode = useSelector(state => state.data.adminMode)
  
  const [openNewFlightDialog, setOpenNewFlightDialog] = useState(false)
  
  const qualifications = useMemo(() => dbQualifications?.filter(q => q.userRef.id === profile.id), [dbQualifications, profile.id])
  const qualifChip = useMemo(() => {
    if (!qualifications) return null
    const statuses = chain(qualifications).filter(q => !q.archived).groupBy(getQualificationStatus).mapValues(val => val.length).values()
    if (statuses.expired)
      return { color: 'error.light', value: statuses.expired }
    else if (statuses.expireSoon)
      return { color: 'orange', value: statuses.expireSoon }
    else return { color: 'success.light', value: qualifications.length }
  }, [qualifications])
  
  return (
    <>
      <Drawer
        variant={isUpMd ? 'permanent' : 'temporary'}
        open={menuStatus !== MENU_STATUS_HIDDEN}
        onClose={() => setMenuStatus(MENU_STATUS_HIDDEN)}
        PaperProps={{
          sx: {
            width: menuStatus === MENU_STATUS_BIG ? drawerWidth : 60,
            backgroundColor: 'grey.900',
            overflowX: 'hidden',
            maxHeight: '100vh',
            transition: menuStatus === MENU_STATUS_BIG
              ? theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              })
              : theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          },
        }}
      >
        <List disablePadding sx={{ height: '100%' }}>
          <ListItem
            onClick={() => navigate(ROUTES.HOME)}
            sx={{
              ...listItemStyles,
              fontWeight: 500,
              color: 'white',
              '&:hover': {
                backgroundColor: 'initial',
              },
            }}
          >
            <Box component='img' src={Logo} alt='MPV' sx={{ height: '28px', ml: '-4px' }} />
            {menuStatus === MENU_STATUS_BIG && <Box component='span' sx={{ ml: 2 }}>
              <Typography fontSize='28px' lineHeight={.9} fontWeight={900} color='grey.400'>
                MPV<Box component='span' color='secondary.main' fontSize='34px' lineHeight={.5}>+</Box>
              </Typography>
            </Box>}
          </ListItem>
          <ListItemButton
            onClick={() => navigate(ROUTES.HOME)}
            sx={{
              py: 2,
              color: pathname === ROUTES.HOME ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              borderBottom: 'solid 1px',
              borderColor: 'grey.700',
              paddingRight: '44px',
              display: 'inline-block',
              width: '80%',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
              '&:after': {
                backgroundColor: 'grey.200',
                content: '\'\'',
                height: '36px',
                opacity: 1,
                position: 'absolute',
                right: 0,
                top: '10px',
                transition: '.075s cubic-bezier(0.4, 0, 1, 1)',
                width: '1px',
              },
            }}
          >
            <StyledListItemIcon><Dashboard /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <StyledListItemText primary='Dashboard' />}
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.PROFILE)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.PROFILE) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              display: 'inline-block',
              width: menuStatus === MENU_STATUS_BIG ? '20%' : '100%',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><Settings /></StyledListItemIcon>
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.ACTIVITIES)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.ACTIVITIES) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><PlayArrow/></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <StyledListItemText primary='Activités' />}
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.FLIGHTS)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.FLIGHTS) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><FlightTakeoff /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <StyledListItemText primary='Vols' />}
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.ACCOUNT)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.ACCOUNT) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><Euro /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <>
              <StyledListItemText primary='Comptes' />
              {!adminMode && <Chip
                variant='outlined'
                size='small'
                label={`${profile.balance > 0 ? '+' : ''}${fixedDecimals(profile.balance)} €`}
                sx={{
                  float: 'right',
                  color: profile.balance > 0 ? 'success.light' : 'error.light',
                  borderColor: profile.balance > 0 ? 'success.light' : 'error.light',
                }}
              />}
            </>}
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.QUALIFICATIONS)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.QUALIFICATIONS) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><Star /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <>
              <StyledListItemText primary='Validités' />
              {qualifChip && !adminMode && <Chip
                size='small'
                variant='outlined'
                label={qualifChip.value}
                sx={{
                  float: 'right',
                  color: qualifChip.color,
                  borderColor: qualifChip.color,
                }}
              />}
            </>}
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.MEMBERS)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.MEMBERS) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><Group /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <StyledListItemText primary='Membres' />}
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.AIRCRAFTS)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.AIRCRAFTS) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><Flight /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <StyledListItemText primary='Avions' />}
          </ListItemButton>
          <ListItemButton
            onClick={() => navigate(ROUTES.PROGRESS_BOOKS)}
            sx={{
              ...listItemStyles,
              color: pathname.includes(ROUTES.PROGRESS_BOOKS) ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <StyledListItemIcon><AssignmentTurnedIn /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <StyledListItemText primary='Livrets de progression' />}
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              !isUpMd && setMenuStatus(MENU_STATUS_HIDDEN)
              setOpenNewFlightDialog(true)
            }}
            sx={{
              color: pathname === openNewFlightDialog ? 'primary.light' : 'rgba(255, 255, 255, 0.7)',
              position: 'absolute',
              bottom: 0,
              borderTop: 'solid 1px',
              width: '100%',
              '&:hover,&:focus': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
              },
              ...listItemStyles,
            }}
          >
            <StyledListItemIcon><Add /></StyledListItemIcon>
            {menuStatus === MENU_STATUS_BIG && <StyledListItemText primary='Saisir un vol' />}
          </ListItemButton>
        </List>
        {menuStatus === MENU_STATUS_BIG && <Typography variant='body2' color='rgba(255, 255, 255, 0.4)' fontSize='.7rem' textAlign='center' sx={{
          position: 'absolute',
          bottom: theme.spacing(1),
          left: 0,
          width: '80%',
        }}>
          Version {window.version}
        </Typography>}
        <Button
          onClick={() => setMenuStatus(prev => {
            if (prev !== MENU_STATUS_BIG) return MENU_STATUS_BIG
            else if (isUpMd) return MENU_STATUS_SMALL
            else return MENU_STATUS_HIDDEN
          })}
          sx={{
            height: '44px',
            color: 'rgba(255, 255, 255, 0.7)',
            display: 'block',
          }}>
          {menuStatus === MENU_STATUS_BIG
            ? <ChevronLeft sx={{ ...chevronStyle, float: 'right' }} />
            : <ChevronRight sx={chevronStyle} />
          }
        </Button>
      </Drawer>
      {openNewFlightDialog && <NewFlightDialog open onClose={() => setOpenNewFlightDialog(false)} />}
    </>
  )
}

NavigationDrawer.propTypes = {
  drawerWidth: PropTypes.number.isRequired,
  menuStatus: PropTypes.string.isRequired,
  setMenuStatus: PropTypes.func.isRequired,
}

export default NavigationDrawer
